import { Locales } from '@griply/types';
import Typography from '@mui/material/Typography';
import { useUpdateUser } from 'features/user';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'shared/assets/icons';
import { Alert, Colors as AlertColors } from 'shared/components/ui/alert';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { Toggle } from 'shared/components/ui/toggle';
import { Positions, Tooltip } from 'shared/components/ui/tooltip';
import { useCachedPushNotificationTokenQuery } from 'shared/hooks/queries/use-cached-push-notification-token-query';
import { useCreatePushNotificationMutation } from 'shared/hooks/queries/use-create-push-notification-mutation';
import { useDeletePushNotificationMutation } from 'shared/hooks/queries/use-delete-push-notification-mutation';
import { useNotificationPermission } from 'shared/hooks/use-notification-permission';
import { useUser } from 'shared/hooks/use-user';
import { Notifications } from 'shared/types/user-settings';

import * as Styled from './settings-form.style';

const InfoTooltip: React.FC<{ title: string }> = ({ title }) => {
  const ref = React.useRef<HTMLDivElement>(null);

  return (
    <>
      <Styled.InfoIconWrapper ref={ref}>
        <Info />
      </Styled.InfoIconWrapper>
      <Tooltip
        label={title}
        referenceElement={ref}
        position={Positions.Top}
        isVisible={undefined}
      />
    </>
  );
};

export const NotificationsToggles: React.FC = () => {
  const { t } = useTranslation();
  const user = useUser();
  const language = user?.settings?.language;
  const notifications = user?.settings?.notifications;

  const [permissions, requestNotificationPermission] =
    useNotificationPermission();
  const { data: pushNotificationToken } = useCachedPushNotificationTokenQuery(
    user?.uid,
  );

  const { mutateAsync: createPushToken } = useCreatePushNotificationMutation();
  const { mutateAsync: deletePushToken } = useDeletePushNotificationMutation();
  const { submit: updateUser } = useUpdateUser();

  const onTogglePush = async () => {
    if (!user?.uid) {
      return;
    }
    if (!pushNotificationToken) {
      await createPushToken({
        userId: user.uid,
        locale: language ?? Locales.EnUS,
      });
      return;
    }

    await deletePushToken({ token: pushNotificationToken });
  };

  const onChangeNotification = ({
    target,
  }: React.ChangeEvent<HTMLInputElement>) => {
    const categoryKey = target.name as keyof Notifications;
    const categoryOption = target.value as keyof Notifications['taskReminder'];
    const category = notifications?.[categoryKey] ?? {};
    const oldValue = category?.[categoryOption] ?? false;

    updateUser(
      {
        notifications: {
          ...(notifications ?? {}),
          [categoryKey]: {
            ...category,
            [categoryOption]: !oldValue,
          },
        },
      },
      user,
    );
  };

  return (
    <>
      <Styled.SplitSectionRow>
        <Typography variant="body2" component="span">
          {t(
            window.electron
              ? 'forms.user-settings.sections.notifications.receive-in-app'
              : 'forms.user-settings.sections.notifications.receive-in-browser',
          )}
        </Typography>
        <Toggle
          checked={!!pushNotificationToken}
          onChange={onTogglePush}
          value="desktop-push"
        />
      </Styled.SplitSectionRow>

      {permissions === 'prompt' && (
        <Alert
          startIcon={Info}
          color={AlertColors.Warning}
          action={
            <Button
              variant={Variants.Outlined}
              color={Colors.Warning}
              size={Sizes.Small}
              onClick={requestNotificationPermission}
            >
              {t(
                'forms.user-settings.sections.notifications.warning.undecided.activate-button.label',
              )}
            </Button>
          }
        >
          <Typography variant="overline" component="span">
            {t(
              'forms.user-settings.sections.notifications.warning.undecided.message.label',
            )}
          </Typography>
        </Alert>
      )}

      {permissions === 'denied' && (
        <Alert startIcon={Info} color={AlertColors.Warning}>
          <Typography variant="overline" component="span">
            {t(
              'forms.user-settings.sections.notifications.warning.denied.message.label',
            )}
          </Typography>
        </Alert>
      )}

      {!!pushNotificationToken && (
        <>
          <Styled.SplitSectionRow>
            <Styled.LabelContainer>
              <Typography variant="body2" component="span">
                {t(
                  'forms.user-settings.sections.notifications.fields.task-reminder.label',
                )}
              </Typography>
              <InfoTooltip
                title={t(
                  'forms.user-settings.sections.notifications.fields.task-reminder.description',
                )}
              />
            </Styled.LabelContainer>
            <Toggle
              name="taskReminder"
              value="desktop"
              checked={!!notifications?.taskReminder?.desktop}
              onChange={onChangeNotification}
            />
          </Styled.SplitSectionRow>

          <Styled.SplitSectionRow>
            <Styled.LabelContainer>
              <Typography variant="body2" component="span">
                {t(
                  'forms.user-settings.sections.notifications.fields.task-summary.label',
                )}
              </Typography>
              <InfoTooltip
                title={t(
                  'forms.user-settings.sections.notifications.fields.task-summary.description',
                )}
              />
            </Styled.LabelContainer>
            <Toggle
              name="taskSummary"
              value="desktop"
              checked={!!notifications?.taskSummary?.desktop}
              onChange={onChangeNotification}
            />
          </Styled.SplitSectionRow>

          <Styled.SplitSectionRow>
            <Styled.LabelContainer>
              <Typography variant="body2" component="span">
                {t(
                  'forms.user-settings.sections.notifications.fields.weekly-review.label',
                )}
              </Typography>
              <InfoTooltip
                title={t(
                  'forms.user-settings.sections.notifications.fields.weekly-review.description',
                )}
              />
            </Styled.LabelContainer>
            <Toggle
              name="weeklyReview"
              value="desktop"
              checked={!!notifications?.weeklyReview?.desktop}
              onChange={onChangeNotification}
            />
          </Styled.SplitSectionRow>
        </>
      )}
    </>
  );
};
